import { __decorate } from "tslib";
import { Vector3, BufferGeometry, Vector2 } from "three";
import { BoneTypeEnum } from "../Models/CaseModel.js";
/**
 * Defines cached images to use for labels.
 */
export class CachedHTMLImages {
}
CachedHTMLImages.empty = new Image(); // used to fix history problem when we set undefined to imageBottomRight param
__decorate([
    LoadImg("images/labels/lock.png")
], CachedHTMLImages, "lock", void 0);
/**
 * Defines constant data.
 */
export class Consts {
    /**
    * Create the convexity segments (1/3, 1/5, ecc) based on bones
    */
    static get convexityList() {
        if (!this._convexityLateralBones) {
            this._convexityLateralBones = {};
            this._convexityLateralBones[BoneTypeEnum.Femur] = {
                DefaultValue: ConvexityValue.Conv_1_3,
                TopEffected: false,
                BottomEffected: true,
                ConvexList: [ConvexityValue.Conv_1_2, ConvexityValue.Conv_1_3]
            };
            this._convexityLateralBones[BoneTypeEnum.Tibia] = {
                DefaultValue: ConvexityValue.Conv_1_5,
                TopEffected: true,
                BottomEffected: false,
                ConvexList: [ConvexityValue.Conv_1_2, ConvexityValue.Conv_1_5]
            };
            this._convexityLateralBones[BoneTypeEnum.LongLeg] = {
                DefaultValue: ConvexityValue.Conv_1_2,
                TopEffected: true,
                BottomEffected: true,
                ConvexList: [ConvexityValue.Conv_1_2, ConvexityValue.Conv_1_3, ConvexityValue.Conv_1_5]
            };
            this._convexityLateralBones[BoneTypeEnum.Ankle] = {
                DefaultValue: ConvexityValue.Conv_1_2,
                TopEffected: true,
                BottomEffected: false,
                ConvexList: [ConvexityValue.Conv_1_2, ConvexityValue.Conv_1_5]
            };
        }
        return this._convexityLateralBones;
    }
    /**
    *
    */
    static get anatomicalAxisLengths() {
        if (!this._anatomicalAxisLengths) {
            this._anatomicalAxisLengths = {};
            this._anatomicalAxisLengths[BoneTypeEnum.LongLeg] = {
                AP: { ref: { A1A2: 80, B1B2: 30 }, mov: { A1A2: 80, B1B2: 30 } },
                LT: { ref: { A1A2: 60, B1B2: 30 }, mov: { A1A2: 60, B1B2: 30 } },
            };
            this._anatomicalAxisLengths[BoneTypeEnum.Femur] = {
                AP: { ref: { A1A2: 30, B1B2: 30 }, mov: { A1A2: 80, B1B2: 30 } },
                LT: { ref: { A1A2: 30, B1B2: 30 }, mov: { A1A2: 60, B1B2: 30 } },
            };
            this._anatomicalAxisLengths[BoneTypeEnum.Tibia] = {
                AP: { ref: { A1A2: 80, B1B2: 30 }, mov: { A1A2: 40, B1B2: 30 } },
                LT: { ref: { A1A2: 50, B1B2: 30 }, mov: { A1A2: 40, B1B2: 30 } },
            };
            this._anatomicalAxisLengths[BoneTypeEnum.Ankle] = {
                AP: { ref: { A1A2: 80, B1B2: 30 }, mov: { A1A2: 30, B1B2: 30 } },
                LT: { ref: { A1A2: 50, B1B2: 30 }, mov: { A1A2: 120, B1B2: 30 } },
            };
            this._anatomicalAxisLengths[BoneTypeEnum.Forefoot] = {
                AP: { ref: { A1A2: 30, B1B2: 30 }, mov: { A1A2: 30, B1B2: 30 } },
                LT: { ref: { A1A2: 30, B1B2: 30 }, mov: { A1A2: 20, B1B2: 20 } },
            };
            this._anatomicalAxisLengths[BoneTypeEnum.Hindfoot] = {
                AP: { ref: { A1A2: 30, B1B2: 30 }, mov: { A1A2: 30, B1B2: 30 } },
                LT: { ref: { A1A2: 30, B1B2: 30 }, mov: { A1A2: 20, B1B2: 20 } },
            };
        }
        return this._anatomicalAxisLengths;
    }
}
/**
 * Scale factor for points.
 */
Consts.scaleFactorPoints = 2.5;
/**
 *  Scale factor for labels.
 */
Consts.scaleFactorLabel = 3.5;
/**
 * Empty geometry used to avoid exception with null geometries before load it async. Don't use it when you restore geometries data with Save attribute because this is shared.
 */
Consts.emptyGeometry = new BufferGeometry();
/**
 * Empty vector used to calculations when you need the origin. This should never be updated.
 */
Consts.emptyVector2 = new Vector2();
/**
 * Empty vector used to calculations when you need the origin. This should never be updated.
 */
Consts.emptyVector3 = new Vector3();
/**
 * Temporary vector used for calculations.
 */
Consts.tempVector2 = new Vector2();
/**
 * Temporary vector used for calculations.
 */
Consts.tempVector3 = new Vector3();
/**
 * Plane normal direction.
 */
Consts.planeNormal = new Vector3(0, 0, 1);
/**
 * Horizontal direction, doesn't change with camera rotation.
 */
Consts.horDir = new Vector3(1, 0, 0);
/**
 * Vertical direction, doesn't change with camera rotation.
 */
Consts.verDir = new Vector3(0, 1, 0);
/**
 * Paley angles range values for mechanical axis.
 */
Consts.paleyAngles = {
    AP: {
        MAD: {
            min: 0,
            max: 7,
        },
        MNSA: {
            min: 124,
            max: 136,
        },
        mLPFA: {
            min: 85,
            max: 95,
            standard: 90
        },
        mLDFA: {
            min: 85,
            max: 90,
            standard: 88
        },
        MPFA: {
            min: 80,
            max: 89
        },
        MPTA: {
            min: 85,
            max: 90,
            standard: 87
        },
        aLDFA: {
            min: 79,
            max: 83
        },
        JLCA: {
            min: 0,
            max: 2
        },
        KneeTransl: {
            min: 0,
            max: 2
        },
        LDTA: {
            min: 86,
            max: 92,
            standard: 89
        }
    },
    LT: {
        aNSA: {
            min: 165,
            max: 175,
            standard: 170
        },
        PDFA: {
            min: 79,
            max: 87,
            standard: 83
        },
        PPTA: {
            min: 77,
            max: 84,
            standard: 81
        },
        ADTA: {
            min: 78,
            max: 82,
            standard: 80
        },
        KneeTransl: {
            min: 0,
            max: 2
        }
    }
};
/**
 * Paley angles range values for anatomical axis.
 */
Consts.AApaleyAngles = {
    AP: {
        Femur: { Proximal: { ref: 84, mov: 99 }, Distal: { ref: 99, mov: 84 } },
        Tibia: { Proximal: { ref: 87, mov: 91 }, Distal: { ref: 91, mov: 87 } }
    },
    LT: {
        Femur: { Proximal: { ref: 90, mov: 83 }, Distal: { ref: 83, mov: 90 } },
        Tibia: { Proximal: { ref: 81, mov: 100 }, Distal: { ref: 100, mov: 81 } }
    }
};
/**
 * List of possible segment convexity
 */
export var ConvexityValue;
(function (ConvexityValue) {
    ConvexityValue[ConvexityValue["Conv_1_5"] = 0.2] = "Conv_1_5";
    ConvexityValue[ConvexityValue["Conv_1_3"] = 0.3333333333333333] = "Conv_1_3";
    ConvexityValue[ConvexityValue["Conv_1_2"] = 0.5] = "Conv_1_2";
})(ConvexityValue || (ConvexityValue = {}));
/**
 * Attribute used to load an image to use as background for labels.
 */
export function LoadImg(path) {
    return function (target, propertyKey, descriptor) {
        const image = new Image();
        image.src = path;
        target[propertyKey] = image;
    };
}
//# sourceMappingURL=Consts.js.map